import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Copy, Check } from "lucide-react";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const botUrl = (token) => `${window.location.origin}/b/${token}`;
export const embedSnippet = (token) => `<iframe src="${botUrl(token)}?embed=1" width="100%" height="640" style="border:1px solid #ddd;border-radius:8px" title="DocuMind chatbot"></iframe>`;

export function CopyField({ value, testId, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Copied"); }
    catch { toast.error("Couldn't copy — select the text and copy it manually"); }
  };
  return (
    <div>
      {label && <div className="font-mono-label text-muted-foreground mb-1">{label}</div>}
      <div className="flex gap-1">
        <Input readOnly data-testid={testId} value={value} onFocus={(e) => e.target.select()} className="h-9 rounded-sm font-mono text-xs" />
        <Button type="button" variant="outline" size="sm" data-testid={`${testId}-copy`} onClick={copy} className="h-9 rounded-sm shrink-0">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

export default function BotCreateDialog({ open, onOpenChange, docs, onCreated }) {
  const [models, setModels] = useState([]);
  const [name, setName] = useState("");
  const [welcome, setWelcome] = useState("");
  const [suggested, setSuggested] = useState(["", "", ""]);
  const [model, setModel] = useState("auto");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/chat/models").then((r) => setModels(r.data)).catch(() => {});
    const first = docs[0]?.filename?.replace(/\.[^.]+$/, "") || "My documents";
    setName(docs.length > 1 ? `${first} + ${docs.length - 1} more` : first);
    setWelcome(`Hi! Ask me anything about ${docs.length > 1 ? "these documents" : first} — every answer points to the page it came from.`);
    setSuggested(["What is this document about?", "Summarize the key points.", ""]);
    setModel("auto");
    setCreated(null);
  }, [open, docs]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/bots", { document_ids: docs.map((d) => d.id), name, welcome, suggested: suggested.filter((s) => s.trim()), model });
      setCreated(data);
      onCreated?.(data);
      toast.success("Chatbot published");
    } catch (err) {
      toast.error(errText(err, "Couldn't publish the chatbot"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="bot-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2"><Bot className="w-5 h-5 text-primary" /> {created ? "Your chatbot is live" : "Publish as a chatbot"}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-4" data-testid="bot-created">
            <p className="text-sm text-muted-foreground">Anyone with the link can sign in to DocuMind and ask <strong className="text-foreground">{created.name}</strong> questions. Each visitor gets 20 questions a day.</p>
            <CopyField label="Share link" value={botUrl(created.token)} testId="bot-link" />
            <CopyField label="Embed on your site" value={embedSnippet(created.token)} testId="bot-embed" />
            <div className="flex gap-2">
              <Button asChild className="rounded-sm bg-foreground text-background hover:bg-primary btn-invert"><a href={botUrl(created.token)} target="_blank" rel="noreferrer" data-testid="bot-open-link">Open chatbot →</a></Button>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="bot-create-close">Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-muted-foreground">Based on: {docs.map((d) => d.filename).join(", ")}</p>
            <div>
              <Label htmlFor="bot-name" className="font-mono-label">Chatbot name</Label>
              <Input id="bot-name" data-testid="bot-name-input" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label htmlFor="bot-welcome" className="font-mono-label">Welcome message</Label>
              <Textarea id="bot-welcome" data-testid="bot-welcome-input" maxLength={400} rows={2} value={welcome} onChange={(e) => setWelcome(e.target.value)} className="rounded-sm mt-1.5 resize-none" />
            </div>
            <div>
              <Label className="font-mono-label">Suggested questions (up to 3)</Label>
              <div className="space-y-1.5 mt-1.5">
                {suggested.map((s, i) => (
                  <Input key={i} data-testid={`bot-suggested-${i}`} maxLength={160} placeholder={`Suggestion ${i + 1}`} value={s}
                    onChange={(e) => setSuggested(suggested.map((x, j) => (j === i ? e.target.value : x)))} className="rounded-sm h-9" />
                ))}
              </div>
            </div>
            <div>
              <Label className="font-mono-label">Model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid="bot-model-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-sm">
                  {models.map((m) => <SelectItem key={m.key} value={m.key} data-testid={`bot-model-${m.key}`}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="bot-create-submit" disabled={saving} className="w-full rounded-sm bg-foreground text-background hover:bg-primary btn-invert">
              {saving ? "Publishing…" : "Publish chatbot →"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
